import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { Component, ElementRef, Inject, Input, NgZone, PLATFORM_ID, ViewChild } from '@angular/core';
import type { IconConfig } from '@core-mkt/interfaces/icons';
import { IconService } from '@core-mkt/services/iconService/icon.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import SwiperCore from 'swiper';
import { Keyboard, Navigation } from 'swiper/modules';
import type { Swiper, SwiperOptions } from 'swiper/types';
import type { SlideCard } from './slider-card';
import { SliderCardSection } from './slider-card';

@Component({
  selector: 'slider-card',
  templateUrl: './slider-card.component.html',
  styleUrls: ['./slider-card.component.scss'],
  standalone: true,
  imports: [CommonModule, VisionButtonComponent, LazyImgDirective],
})
export class SliderCardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: SliderCardSection;
  theme: ThemeInterface;
  cellClass = '';
  sliderId = 'slider-' + Math.random().toString(16).slice(2);
  showTitle = false;
  showDescription = false;
  centerCards = '';
  centerTransform = '';
  prevIcon: IconConfig | null = null;
  nextIcon: IconConfig | null = null;
  @ViewChild('swiperEl') swiperEl?: ElementRef<HTMLElement>;
  @ViewChild('prevEl') prevEl?: ElementRef<HTMLElement>;
  @ViewChild('nextEl') nextEl?: ElementRef<HTMLElement>;
  swiper?: SwiperCore;
  public swiperConfig: SwiperOptions = {
    modules: [Navigation, Keyboard],
    // The removed swiper/angular wrapper always set observer: true; Swiper 12 defaults it to
    // false. Restored so slides re-rendered after init are still picked up, matching v8.
    observer: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    slidesPerView: 'auto',
    // Deliberately inert: FreeMode is not in modules[] above, so this does nothing.
    // It was equally inert on swiper 8 (the module was never registered there either) --
    // registering it now would change scroll physics, which is out of scope for a migration.
    freeMode: true,
    initialSlide: 0,
    centerInsufficientSlides: true,
    centeredSlides: true,
    breakpoints: {
      640: {
        centeredSlides: false,
      },
    },
  };

  constructor(
    private redactor: WysiwygRedactorService,
    private themeParser: ThemeParserService,
    private iconService: IconService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone,
  ) {}

  get carouselCellClass(): string {
    return this.cellClass;
  }

  get sliderDescription(): string {
    return this.data.sliderDescription;
  }

  get slides(): SlideCard[] {
    return this.data.slidesCards;
  }

  get sliderTitle(): string {
    return this.data.sliderTitle;
  }

  get displayAllCardMobile(): boolean {
    return this.data.displayAllCardMobile;
  }

  get showNavigationButtons(): boolean {
    return this.data.slidesCards && this.data.slidesCards.length > 1;
  }

  get hideArrowsOnDesktop(): boolean {
    return this.data.slidesCards && this.data.slidesCards.length < 4;
  }

  ngOnInit(): void {
    this.showTitle = this.data.sliderTitle !== null && this.data.sliderTitle.length > 0;
    this.showDescription = this.data.sliderDescription !== null && this.data.sliderDescription.length > 0;
    this.data.sliderTitle = this.redactor.bypassSanitizer(this.data.sliderTitle);
    this.data.sliderDescription = this.redactor.bypassSanitizer(this.data.sliderDescription);
    this.data.slidesCards.forEach((slide) => {
      // SlideCard.slideContent is typed `any` in the shared model; it always holds an HTML string here.
      const sanitizedContent = this.redactor.bypassSanitizer(slide.slideContent as string);
      slide.slideContent = sanitizedContent;
      slide.hasBottomContent = new RegExp(`class=["'][^"']*\\bbottom-content\\b[^"']*["']`).test(sanitizedContent);
    });
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: null,
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.cellClass = this.data.slidesCards.length > 3 ? 'carousel-cell-small' : 'carousel-cell-big';
  }

  buttonData(slide: SlideCard): VisionButton {
    return {
      bgTheme: this.theme.root,
      buttonLink: slide.slideButtonLink,
      buttonText: slide.slideButtonLabel,
      buttonStyle: slide.slideButtonStyle,
      newTab: slide.slideButtonNewTab,
      nofollow: slide.slideButtonNofollow,
      noopener: false,
      noreferrer: false,
      fullWidth: false,
    };
  }

  addFullHeightToCarouselCell(): void {
    const carouselCells = this.document.querySelectorAll('.carousel-cell');
    carouselCells.forEach((cell) => {
      cell.classList.add('h-full');
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swiperEl) return;
    // Swiper Core does not create the navigation buttons the way the removed
    // swiper/angular component did, so they are authored in the template and
    // handed over as live elements here.
    // Swiper is constructed outside Angular's zone. The removed swiper/angular wrapper did
    // the same (swiper_angular.mjs), and it matters here: Swiper 12 registers pointermove /
    // touchmove listeners on `document`, and this app runs provideZoneChangeDetection() with
    // no event coalescing -- in-zone, every mouse move anywhere on the page would run a full
    // change-detection tick per carousel. Nothing in this component needs Angular to re-render
    // in response to a Swiper event.
    this.ngZone.runOutsideAngular(() => {
      this.swiper = new SwiperCore(this.swiperEl.nativeElement, {
        ...this.swiperConfig,
        navigation: { prevEl: this.prevEl?.nativeElement, nextEl: this.nextEl?.nativeElement },
      });
      this.onSwiper(this.swiper);
    });
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
  }

  onSwiper(swiper: Swiper): void {
    // Set accessibility attributes to the navigation buttons
    const navigation = swiper?.params?.navigation;
    if (navigation && typeof navigation === 'object' && 'prevEl' in navigation && 'nextEl' in navigation) {
      // Swiper resolves prevEl/nextEl to the live HTMLElement once initialized; the CSSSelector
      // form only exists pre-init, so narrow to HTMLElement for the DOM interactions below.
      const prevEl = navigation.prevEl instanceof HTMLElement ? navigation.prevEl : null;
      const nextEl = navigation.nextEl instanceof HTMLElement ? navigation.nextEl : null;

      if (this.showNavigationButtons) {
        prevEl?.setAttribute('tabindex', '0');
        prevEl?.setAttribute('role', 'button');
        prevEl?.setAttribute('aria-label', 'Previous slide');
        // No click handler here on purpose: the Navigation module binds its own click
        // listener to prevEl/nextEl. A hand-rolled one fires alongside it and advances
        // two slides per tap (EPS-4821). Keydown is still ours -- a div[role=button]
        // does not synthesize a click from Enter/Space, so it is not duplicated.
        prevEl.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            swiper.slidePrev();
          }
        });
        nextEl?.setAttribute('tabindex', '0');
        nextEl?.setAttribute('role', 'button');
        nextEl?.setAttribute('aria-label', 'Next slide');
        // Add keyboard navigation to the navigation buttons when user focus on them
        nextEl.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            swiper.slideNext();
          }
        });

        //this is a cheat to add svg requested by design
        this.prevIcon = this.iconService.getBrandIcon('slider-left');
        this.nextIcon = this.iconService.getBrandIcon('slider-right');
        if (this.prevIcon && this.nextIcon) {
          prevEl.innerHTML = this.prevIcon.icon;
          nextEl.innerHTML = this.nextIcon.icon;
        }

        if (prevEl) prevEl.style.display = 'block';
        if (nextEl) nextEl.style.display = 'block';
      } else {
        if (prevEl) prevEl.style.display = 'none';
        if (nextEl) nextEl.style.display = 'none';
      }
    }
  }
}
